import { useCallback, useEffect, useMemo, useRef, useState, type TouchEvent } from 'react';

import {
  LANG_CODES,
  LANG_NAMES,
  Rating,
  type Concept,
  type Lang,
  type ReviewCard,
  type ReviewQueue,
} from '@lexio/shared';

import { ConceptEditor } from '../components/ConceptEditor';
import { fetchQueue, flushPending, readPending, recordAnswer, setPrefs } from '../lib/api';

interface Props {
  onNavigate: (page: string) => void;
  /** Jazyky, ktere se maji na odkryte karte ukazat navic "vedle sebe". */
  showLangs: Lang[];
}

const RATING_LABELS: { rating: Rating; label: string; hint: string; className: string }[] = [
  { rating: Rating.Again, label: 'Nevěděl', hint: '1', className: 'again' },
  { rating: Rating.Hard, label: 'Těžké', hint: '2', className: 'hard' },
  { rating: Rating.Good, label: 'Věděl', hint: '3', className: 'good' },
  { rating: Rating.Easy, label: 'Snadné', hint: '4', className: 'easy' },
];

/** Kolik novych karet pridat nad denni limit, kdyz se chce uzivatel ucit dal. */
const EXTRA_NEW_BATCH = 20;

/** Minimalni vodorovny posun prstu, ktery se bere jako swipe na dalsi/predchozi kartu. */
const SWIPE_PX = 60;

/** Prevede interval ve dnech na lidsky citelny text pod tlacitkem. */
function formatInterval(days: number): string {
  if (days <= 0) return 'dnes';
  if (days === 1) return 'zítra';
  if (days < 30) return days <= 4 ? `${days} dny` : `${days} dní`;
  if (days < 365) {
    const m = Math.round(days / 30);
    return m === 1 ? '1 měsíc' : m < 5 ? `${m} měsíce` : `${m} měsíců`;
  }
  return `${Math.round((days / 365) * 10) / 10} roku`;
}

export function Review({ onNavigate, showLangs }: Props) {
  const [queue, setQueue] = useState<ReviewQueue | null>(null);
  const [index, setIndex] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [offline, setOffline] = useState(false);
  const [pendingCount, setPendingCount] = useState(readPending().length);
  const [doneCount, setDoneCount] = useState(0);
  /** Co uz uzivatel s kartou v teto davce udelal - po navratu zpet se nehodnoti znovu. */
  const [answered, setAnswered] = useState<Record<string, string>>({});
  const [editing, setEditing] = useState(false);

  const shownAt = useRef(Date.now());
  const touchX = useRef<number | null>(null);

  const load = useCallback(async (extraNew = 0) => {
    setLoading(true);
    setError(null);
    try {
      const { queue: q, offline: off } = await fetchQueue(extraNew);
      setQueue(q);
      setOffline(off);
      setIndex(0);
      setAnswered({});
      setEditing(false);
      setRevealed(false);
      shownAt.current = Date.now();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Frontu se nepodařilo načíst');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const onOnline = async () => {
      const { pending } = await flushPending();
      setPendingCount(pending);
      setOffline(false);
    };
    const onOffline = () => setOffline(true);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  const card: ReviewCard | undefined = queue?.cards[index];

  const cardDone = card ? answered[card.cardId] : undefined;

  const next = useCallback(() => {
    setIndex((i) => i + 1);
    setRevealed(false);
    setEditing(false);
    shownAt.current = Date.now();
  }, []);

  /** Zpet na predchozi kartu - jen k nahlednuti, uz ohodnocena se nehodnoti znovu. */
  const prev = useCallback(() => {
    setIndex((i) => Math.max(i - 1, 0));
    setRevealed(true);
    setEditing(false);
  }, []);

  const mark = useCallback((cardId: string, label: string) => {
    setAnswered((a) => ({ ...a, [cardId]: label }));
  }, []);

  const answer = useCallback(
    async (rating: Rating) => {
      if (!card || answered[card.cardId]) return;
      mark(card.cardId, RATING_LABELS.find((r) => r.rating === rating)?.label ?? '');
      next();
      setDoneCount((n) => n + 1);

      const { pending } = await recordAnswer({
        clientId: crypto.randomUUID(),
        cardId: card.cardId,
        rating,
        answeredAt: new Date().toISOString(),
        elapsedMs: Date.now() - shownAt.current,
      });
      setPendingCount(pending);
    },
    [card, answered, mark, next],
  );

  const markKnown = useCallback(async () => {
    if (!card) return;
    const id = card.concept.id;
    mark(card.cardId, 'Už umím');
    next();
    try {
      await setPrefs(id, { known: true });
    } catch {
      // Offline: prioritizace neni kriticka, uzivatel ji muze zopakovat.
    }
  }, [card, mark, next]);

  const suppress = useCallback(async () => {
    if (!card) return;
    const id = card.concept.id;
    mark(card.cardId, 'Nezajímá mě');
    next();
    try {
      await setPrefs(id, { suppressed: true });
    } catch {
      // viz vyse
    }
  }, [card, mark, next]);

  /** Ulozena uprava ze slovniku se promitne do vsech karet tehoz konceptu ve fronte. */
  const applyEdit = useCallback((saved: Concept) => {
    setQueue((q) =>
      q ? { ...q, cards: q.cards.map((c) => (c.concept.id === saved.id ? { ...c, concept: saved } : c)) } : q,
    );
    setEditing(false);
    setRevealed(true);
  }, []);

  // Klavesove zkratky pro dril na PC.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (editing || !card) return;
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement ||
        e.target instanceof HTMLSelectElement
      )
        return;

      if (!revealed && (e.key === ' ' || e.key === 'Enter')) {
        e.preventDefault();
        setRevealed(true);
        return;
      }
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        next();
        return;
      }
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        prev();
        return;
      }
      if (cardDone) return;
      // Hodnotit jde i bez odkryti - kdo slovicko zna, nemusi nejdriv klikat na preklad.
      if (['1', '2', '3', '4'].includes(e.key)) {
        e.preventDefault();
        void answer(Number(e.key) as Rating);
        return;
      }
      if (e.key === 'k') void markKnown();
      if (e.key === 'x') void suppress();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [card, cardDone, editing, revealed, answer, markKnown, suppress, next, prev]);

  const onTouchStart = (e: TouchEvent) => {
    touchX.current = e.touches[0]?.clientX ?? null;
  };

  // Swipe doleva = dalsi karta, doprava = predchozi.
  const onTouchEnd = (e: TouchEvent) => {
    const start = touchX.current;
    touchX.current = null;
    const end = e.changedTouches[0]?.clientX;
    if (start === null || end === undefined || editing) return;
    const dx = end - start;
    if (dx <= -SWIPE_PX) next();
    else if (dx >= SWIPE_PX && index > 0) prev();
  };

  const progress = useMemo(() => {
    if (!queue) return 0;
    return queue.cards.length === 0 ? 100 : Math.round((index / queue.cards.length) * 100);
  }, [queue, index]);

  if (loading) return <div className="center muted">Načítám…</div>;

  if (error) {
    return (
      <div className="center">
        <p className="error">{error}</p>
        <button className="btn" onClick={() => void load()}>
          Zkusit znovu
        </button>
      </div>
    );
  }

  if (!card) {
    return (
      <div className="center done">
        <div className="done-icon">✓</div>
        <h2>Hotovo pro teď</h2>
        <p className="muted">
          {doneCount > 0 ? `Zvládl jsi ${doneCount} karet.` : 'Žádné karty nečekají.'}
          {queue && queue.newRemaining > 0
            ? ` Nových do denního limitu zbývá ${queue.newRemaining}.`
            : ' Denní limit nových slovíček je vyčerpaný (dá se změnit v Nastavení).'}
        </p>
        {pendingCount > 0 && <p className="warn">Čeká na odeslání: {pendingCount} odpovědí</p>}
        <div className="row">
          <button className="btn primary" onClick={() => void load(EXTRA_NEW_BATCH)}>
            Učit se dál (+{EXTRA_NEW_BATCH} nových)
          </button>
          <button className="btn" onClick={() => void load()}>
            Načíst znovu
          </button>
          {queue && queue.cards.length > 0 && (
            <button className="btn ghost" onClick={prev}>
              ‹ Zpět
            </button>
          )}
          <button className="btn ghost" onClick={() => onNavigate('stats')}>
            Statistiky
          </button>
        </div>
      </div>
    );
  }

  const { concept, direction } = card;
  const front = concept.terms[direction.from];
  const back = concept.terms[direction.to];

  // Jazyky "vedle sebe": co si uzivatel zvolil, mimo ty dva ve smeru.
  const extraLangs = showLangs.filter(
    (l) => l !== direction.from && l !== direction.to && concept.terms[l],
  );

  const example = concept.examples[0];

  return (
    <div className="review">
      <div className="progress-bar" aria-hidden="true">
        <div className="progress-fill" style={{ width: `${progress}%` }} />
      </div>

      <div className="review-meta">
        <span>
          {index + 1} / {queue?.cards.length}
        </span>
        <span className="tags">
          <span className="tag dir">
            {LANG_CODES[direction.from]} → {LANG_CODES[direction.to]}
          </span>
          {card.state === 'new' && <span className="tag new">nové</span>}
          {concept.level && <span className="tag">{concept.level}</span>}
          {offline && <span className="tag warn">offline</span>}
        </span>
      </div>

      <div
        className="card"
        onClick={() => !revealed && !editing && setRevealed(true)}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        <div className="lang-label">{LANG_NAMES[direction.from]}</div>
        <div className="term">{front?.term ?? '—'}</div>
        {front?.ipa && <div className="ipa">{front.ipa}</div>}
        {front && front.register !== 'neutral' && <div className="register">{front.register}</div>}

        {cardDone && <span className="tag done-tag">{cardDone}</span>}

        {editing ? (
          <ConceptEditor concept={concept} onSaved={applyEdit} onCancel={() => setEditing(false)} />
        ) : !revealed ? (
          <button className="btn reveal" onClick={() => setRevealed(true)}>
            Ukázat překlad <kbd>mezerník</kbd>
          </button>
        ) : (
          <div className="answer">
            <div className="lang-label">{LANG_NAMES[direction.to]}</div>
            <div className="translations">{back?.term}</div>
            {back && back.variants.length > 0 && (
              <div className="variants">{back.variants.join(' · ')}</div>
            )}

            {extraLangs.length > 0 && (
              <div className="other-langs">
                {extraLangs.map((l) => (
                  <div className="other-lang" key={l}>
                    <span className="other-code">{LANG_CODES[l]}</span>
                    <span className="other-term">{concept.terms[l]?.term}</span>
                  </div>
                ))}
              </div>
            )}

            {example && (
              <div className="example">
                <div className="ex-en">{example[direction.from]}</div>
                <div className="ex-cs">{example[direction.to]}</div>
                {extraLangs.map((l) =>
                  example[l] ? (
                    <div className="ex-extra" key={l}>
                      <span className="other-code">{LANG_CODES[l]}</span> {example[l]}
                    </div>
                  ) : null,
                )}
              </div>
            )}

            {back && back.collocations.length > 0 && (
              <div className="collocations">
                {back.collocations.map((c) => (
                  <span className="chip" key={c}>
                    {c}
                  </span>
                ))}
              </div>
            )}

            {back?.note && <div className="note">💡 {back.note}</div>}
            {front?.note && front.note !== back?.note && <div className="note">💡 {front.note}</div>}
          </div>
        )}
      </div>

      {!editing && (
        <>
          {/* Hodnoceni je videt i pred odkrytim - kdo slovicko zna, nemusi nejdriv odkryvat. */}
          {!cardDone && (
            <div className="ratings">
              {RATING_LABELS.map((r) => (
                <button
                  key={r.rating}
                  className={`btn rating ${r.className}`}
                  onClick={() => void answer(r.rating)}
                >
                  <span className="rating-label">{r.label}</span>
                  <span className="rating-interval">{formatInterval(card.intervalPreview[r.rating])}</span>
                  <kbd>{r.hint}</kbd>
                </button>
              ))}
            </div>
          )}

          <div className="row secondary">
            <button className="btn ghost small" disabled={index === 0} onClick={prev}>
              ‹ Zpět <kbd>←</kbd>
            </button>
            <button className="btn ghost small" onClick={next}>
              {cardDone ? 'Dál' : 'Přeskočit'} › <kbd>→</kbd>
            </button>
            <button className="btn ghost small" onClick={() => setEditing(true)}>
              ✎ Upravit
            </button>
          </div>

          {!cardDone && (
            <div className="row secondary">
              <button className="btn ghost small" onClick={() => void markKnown()}>
                Už umím <kbd>k</kbd>
              </button>
              <button className="btn ghost small" onClick={() => void suppress()}>
                Nezajímá mě <kbd>x</kbd>
              </button>
            </div>
          )}
        </>
      )}

      {pendingCount > 0 && <div className="pending-note">čeká na odeslání: {pendingCount}</div>}
    </div>
  );
}
